import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Menu as MenuIcon } from 'lucide-react';
import Menu from './components/Menu';
import UserMenu from './components/UserMenu';
import WelcomeWave from './components/WelcomeWave';
import { getPageTitle } from './navConfig';
import { t, useLanguage, applyAccountLanguage } from './i18n';
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import ProductListPage from './pages/ProductListPage';
import ProductEditPage from './pages/ProductEditPage';
import ProductAddPage from './pages/ProductAddPage';
import ProductImportPage from './pages/ProductImportPage';
import InvoiceListPage from './pages/InvoiceListPage';
import SettingsPage from './pages/SettingsPage';
import PartnersPage from './pages/PartnersPage';
import PartnerAddPage from './pages/PartnerAddPage';
import PartnerListPage from './pages/PartnerListPage';
import InvoicePage from './pages/InvoicePage';
import InvoiceAddPage from './pages/InvoiceAddPage';
import InvoicePreviewPage from './pages/InvoicePreviewPage';
import PaymentsList from './pages/PaymentsList';
import LoginPage from './pages/LoginPage';
import PrivateRoute from './components/PrivateRoute';
import PartnerEditPage from './pages/PartnerEditPage';
import CategoriesListPage from './pages/CategoriesListPage';
import CategoryAddPage from './pages/CategoryAddPage';
import CategoryEditPage from './pages/CategoryEditPage';
import SettingsBussinessPage from './pages/SettingsBussinessPage';
import ManufacturerListPage from './pages/ManufacturerListPage';
import ManufacturerAddPage from './pages/ManufacturerAddPage';
import ManufacturerEditPage from './pages/ManufacturerEditPage';
import OrderAddPage from './pages/OrderAddPage';
import OrderListPage from './pages/OrderListPage';
import DeliveryNoteListPage from './pages/DeliveryNoteListPage';
import DeliveryNoteAddPage from './pages/DeliveryNoteAddPage';
import PreviewPage from './pages/PreviewPage';
import InvoiceHtmlPreviewPage from './pages/InvoiceHtmlPreviewPage';
import WarehouseListPage from './pages/WarehouseListPage';
import WarehouseMovementPage from './pages/WarehouseMovementPage';
import InvoiceSimpleAddPage from './pages/InvoiceSimpleAddPage';
import InvoiceCreatePage from './pages/InvoiceCreatePage';
import { handleUnauthorized } from './utils/auth';

const apiUrl = process.env.REACT_APP_API_URL;

function App() {
  // Subscribing here re-renders the whole app when the language changes
  useLanguage();
  return (
    <Router>
      <AppWithAuthCheck />
    </Router>
  );
}

function AppWithAuthCheck() {
  const location = useLocation();

  useEffect(() => {
    // Only check token if not on /login
    if (location.pathname === '/login') return;

    const checkToken = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }
      const requestedAt = Date.now();
      const response = await fetch(`${apiUrl}/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (handleUnauthorized(response)) return;
      // Follow the language saved on the account (e.g. changed on another device)
      if (response.ok) {
        try {
          const data = await response.json();
          applyAccountLanguage(data?.user?.language, requestedAt);
        } catch { /* ignore malformed body */ }
      }
    };
    checkToken();
  }, [location.pathname]);

  const handleAddProduct = (_product) => {};
  const handleAddPartner = (_partner) => {};

  return (
    <div className="App min-h-screen font-sans bg-gradient-to-br from-blue-100 via-white to-blue-200">
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/preview/:filename" element={<PreviewPage />} />
        <Route path="/preview/invoice/:id" element={<InvoiceHtmlPreviewPage />} />
        <Route path="/preview/:id/:type/:pdfFilename" element={<PreviewPage />} />
        <Route path="/delivery-notes/preview/:id" element={<PreviewPage />} />
        <Route path="/invoices/preview/:id" element={<PreviewPage />} />

        {/* Private Routes */}
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <AppContent onAddProduct={handleAddProduct} onAddPartner={handleAddPartner} />
            </PrivateRoute>
          }
        />
      </Routes>
    </div>
  );
}

function AppContent({ onAddProduct, onAddPartner }) {
  // Get avatar from localStorage or fallback
  const avatarUrl = localStorage.getItem('avatar') || '/images/avatar.png';
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  // Separate from isMenuOpen: that one is the desktop rail's collapsed state,
  // this is the mobile off-canvas overlay. They are independent.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const { section, page } = getPageTitle(location.pathname);
  // No offset on phones — the sidebar is an overlay there, not a rail.
  const sidebarMargin = isMenuOpen ? 'md:ml-48' : 'md:ml-16';

  return (
    <>
      <Menu
        isOpen={isMenuOpen}
        setIsOpen={setIsMenuOpen}
        mobileOpen={mobileNavOpen}
        setMobileOpen={setMobileNavOpen}
      />
      <div className={`flex flex-col h-screen transition-all duration-300 ${sidebarMargin}`}>
        <header
          className="h-16 flex items-center justify-between gap-3 px-4 sm:px-6 shadow shrink-0"
          style={{ backgroundColor: '#415c89' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden text-white shrink-0 p-1 -ml-1"
              title={t('Отвори мени')}
              aria-label={t('Отвори мени')}
            >
              <MenuIcon size={22} />
            </button>
            <div className="min-w-0">
              {location.pathname !== '/' && section && (
                <>
                  {page && <p className="text-xs text-blue-200 truncate">{t(section)}</p>}
                  <h1 className="text-base sm:text-lg font-semibold text-white truncate">{t(page || section)}</h1>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {location.pathname === '/' && (
              // Greeting is the first thing to go when space is tight — and
              // the wave goes with it, or phones see three orphan dots.
              <div className="hidden sm:flex items-center gap-3">
                <span className="text-sm font-semibold text-white">
                  {t('Добредојдовте во Pulse')}
                </span>
                <WelcomeWave size={8} color="#93c5fd" />
              </div>
            )}
          </div>
          <UserMenu userName="Kire" avatarUrl={avatarUrl} />
        </header>
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/products" element={<ProductPage />} />
            <Route path="/products/list" element={<ProductListPage />} />
            <Route path="/products/add" element={<ProductAddPage onAddProduct={onAddProduct} />} />
            <Route path="/products/import" element={<ProductImportPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/partners/list" element={<PartnerListPage />} />
            <Route path="/products/edit/:id" element={<ProductEditPage />} />
            <Route path="/categories/list" element={<CategoriesListPage />} />
            <Route path="/categories/edit/:id" element={<CategoryEditPage />} />
            <Route path="/partners/add" element={<PartnerAddPage onAddPartner={onAddPartner} />} />
            <Route path="/categories/add" element={<CategoryAddPage />} />
            <Route path="/partners/edit/:id" element={<PartnerEditPage />} />
            <Route path="/invoices" element={<InvoicePage />} />
            <Route path="/invoices/list" element={<InvoiceListPage />} />
            <Route path="/invoices/add" element={<InvoiceAddPage />} />
            <Route path="/invoices/edit/:id" element={<InvoiceCreatePage />} />
            <Route path="/invoices/view/:id" element={<InvoicePreviewPage />} />
            <Route path="/payments/list" element={<PaymentsList />} />
            <Route path="/delivery-notes/add" element={<DeliveryNoteAddPage />} />
            <Route path="/delivery-notes/edit/:id" element={<DeliveryNoteAddPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/mybussiness" element={<SettingsBussinessPage />} />
            <Route path="/manufacturer/list" element={<ManufacturerListPage />} />
            <Route path="/manufacturer/add" element={<ManufacturerAddPage />} />
            <Route path="/manufacturer/edit/:id" element={<ManufacturerEditPage />} />
            <Route path="/orders/add" element={<OrderAddPage />} />
            <Route path="/orders/list" element={<OrderListPage />} />
            <Route path="/delivery-notes/list" element={<DeliveryNoteListPage />} />
            <Route path="/warehouse/list" element={<WarehouseListPage />} />
            <Route path="/warehouse/movement" element={<WarehouseMovementPage />} />
            <Route path="/simple-invoice/add" element={<InvoiceSimpleAddPage />} />
            <Route path="/simple-invoice/edit/:id" element={<InvoiceSimpleAddPage />} />
            <Route path="/invoices/create" element={<InvoiceCreatePage />} />
            <Route path="/invoices/create/:id" element={<InvoiceCreatePage />} />
          </Routes>
        </main>
      </div>
    </>
  );
}

export default App;